// Audit tab — recent log entries + a one-click integrity check
// against the server's hash chain.
//
// Filters mirror the server's auditLog arg shape: actor (exact), action
// (LIKE pattern), and sinceDays (windowed). 200 entries is the default
// limit — bumping is server-capped at 2000.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type AuditEntry, type AuditVerifyResult } from "../../lib/rpc";

interface AuditTabProps {
  notify: (msg: string) => void;
}

type RenewIssueAction =
  | "session.renew.invalid_grant"
  | "session.renew.missing_refresh_token"
  | "session.renew.transient_error";

interface RenewIssueSummaryRow {
  ts: string;
  actor: string;
  action: RenewIssueAction;
  sessionFp: string;
  scope: string | null;
  expiresInSec: number | null;
  ageHours: number | null;
  code: string | null;
}

const RENEW_ISSUE_ACTIONS = new Set<RenewIssueAction>([
  "session.renew.invalid_grant",
  "session.renew.missing_refresh_token",
  "session.renew.transient_error",
]);

function detailString(details: AuditEntry["details"], key: string): string | null {
  if (!details || typeof details !== "object") return null;
  const v = (details as Record<string, unknown>)[key];
  return typeof v === "string" && v.trim() ? v : null;
}

function detailNumber(details: AuditEntry["details"], key: string): number | null {
  if (!details || typeof details !== "object") return null;
  const v = (details as Record<string, unknown>)[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function formatExpiry(seconds: number | null): string {
  if (seconds === null || seconds < 0) return "n/a";
  if (seconds < 60) return "<1m";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function issueLabel(row: RenewIssueSummaryRow): string {
  if (row.action === "session.renew.invalid_grant") return "Auth0 refresh token rejected";
  if (row.action === "session.renew.missing_refresh_token") return "No refresh token stored";
  const code = row.code ? ` (${row.code})` : "";
  return `Transient renewal error${code}`;
}

export function AdminAuditTab({ notify }: AuditTabProps) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [renewIssues, setRenewIssues] = useState<RenewIssueSummaryRow[] | null>(null);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [sinceDays, setSinceDays] = useState<number>(7);
  const [verifying, setVerifying] = useState(false);
  const [verify, setVerify] = useState<AuditVerifyResult | null>(null);

  const refresh = async () => {
    try {
      const actorFilter = actor.trim() || undefined;
      const sinceFilter = sinceDays > 0 ? sinceDays : undefined;
      const [list, renewList] = await Promise.all([
        api.adminAuditLog({
          actor: actorFilter,
          action: action.trim() || undefined,
          sinceDays: sinceFilter,
          limit: 200,
        }),
        api.adminAuditLog({
          actor: actorFilter,
          action: "session.renew.%",
          sinceDays: sinceFilter,
          limit: 1000,
        }),
      ]);
      // Server returns oldest-first (the DESC query is `.reverse()`d
      // server-side so the chain-verifier can walk in insertion order).
      // For UI we want newest at the top.
      setEntries(list.slice().sort((a, b) => b.ts.localeCompare(a.ts)));

      const latestBySession = new Map<string, RenewIssueSummaryRow>();
      const sortedRenew = renewList.slice().sort((a, b) => b.ts.localeCompare(a.ts));
      for (const e of sortedRenew) {
        if (!RENEW_ISSUE_ACTIONS.has(e.action as RenewIssueAction)) continue;
        const sessionFp = detailString(e.details, "sessionFp") || "unknown";
        const key = `${e.actor}:${sessionFp}`;
        if (latestBySession.has(key)) continue;
        latestBySession.set(key, {
          ts: e.ts,
          actor: e.actor,
          action: e.action as RenewIssueAction,
          sessionFp,
          scope: detailString(e.details, "scope"),
          expiresInSec: detailNumber(e.details, "expiresInSec"),
          ageHours: detailNumber(e.details, "ageHours"),
          code: detailString(e.details, "code"),
        });
      }
      setRenewIssues(Array.from(latestBySession.values()).sort((a, b) => b.ts.localeCompare(a.ts)));
    } catch (e: any) {
      notify(e?.message || "Couldn't load audit log");
      setEntries([]);
      setRenewIssues([]);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const runVerify = async () => {
    setVerifying(true);
    try {
      const r = await api.adminAuditVerify();
      setVerify(r);
      notify(r.intact ? "Audit chain intact" : "Chain inconsistency detected");
    } catch (e: any) {
      notify(e?.message || "Verify failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="admin-card">
      <h3>Audit log</h3>
      <p className="admin-card-sub">
        Recent actions across the instance. Hash-chained — verify to confirm no row was tampered with after-the-fact.
      </p>

      <div className="admin-audit-filters">
        <input
          className="input admin-mono-input"
          placeholder="actor (exact)"
          value={actor}
          onInput={(e) => setActor((e.currentTarget as HTMLInputElement).value)}
        />
        <input
          className="input admin-mono-input"
          placeholder="action (e.g. login.%  or  notes.save)"
          value={action}
          onInput={(e) => setAction((e.currentTarget as HTMLInputElement).value)}
        />
        <select
          className="input"
          value={sinceDays}
          onChange={(e) => setSinceDays(Number((e.currentTarget as HTMLSelectElement).value))}
        >
          <option value={1}>last 24h</option>
          <option value={7}>last 7 days</option>
          <option value={30}>last 30 days</option>
          <option value={0}>all time</option>
        </select>
        <button className="btn" onClick={refresh} type="button">
          <Icon name="filter" size={13} /> Apply
        </button>
        <button className="btn" onClick={runVerify} disabled={verifying} type="button">
          <Icon name="shield" size={13} /> {verifying ? "Verifying…" : "Verify chain"}
        </button>
      </div>

      {verify && !verify.intact && verify.brokenAt && (
        <div className="admin-banner admin-banner-warn">
          Chain broken at row #{verify.brokenAt.id} ({verify.brokenAt.action} by {verify.brokenAt.actor} on {verify.brokenAt.ts.slice(0, 19)}).
        </div>
      )}
      {verify && verify.intact && (
        <div className="admin-banner admin-banner-ok">Chain intact — every row's hash matches.</div>
      )}

      <div className="admin-audit-diag">
        <h4>Session renew diagnostics</h4>
        <p className="admin-card-sub">
          Latest renewal issue per user and session fingerprint from <span className="admin-user-mono">session.renew.*</span> events.
        </p>
        {renewIssues === null ? (
          <p className="admin-loading">Loading diagnostics…</p>
        ) : renewIssues.length === 0 ? (
          <p className="admin-empty">No renewal issues in this time window.</p>
        ) : (
          <div className="admin-audit-diag-list">
            {renewIssues.map((r) => (
              <div key={`${r.actor}:${r.sessionFp}`} className="admin-audit-diag-row">
                <span className="admin-audit-when">{r.ts.slice(0, 19).replace("T", " ")}</span>
                <span className="admin-user-mono">{r.actor}</span>
                <span className="admin-audit-diag-session">{r.sessionFp}</span>
                <span className="admin-audit-diag-reason">{issueLabel(r)}</span>
                <span className="admin-audit-diag-meta">
                  scope={r.scope || "standard"}
                  {r.ageHours !== null ? ` • age=${r.ageHours}h` : ""}
                  {` • expires=${formatExpiry(r.expiresInSec)}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {entries === null ? (
        <p className="admin-loading">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="admin-empty">No entries match these filters.</p>
      ) : (
        <div className="admin-audit-list">
          {entries.map((e) => (
            <div key={e.id} className="admin-audit-row">
              <span className="admin-audit-when">{e.ts.slice(0, 19).replace("T", " ")}</span>
              <span className="admin-user-mono">{e.actor}</span>
              <span className="admin-audit-action">{e.action}</span>
              <span className="admin-audit-target">{e.target || ""}</span>
              <span className="admin-audit-ip">{e.ip || ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
